import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Grid, TextField } from '@mui/material';

import { Car } from '@/types';
import { useCreateCarMutation } from '@/services';
import { messages } from '@/utils/helpers';
import { SearchCarBrands } from '@/pages/CarBrands/components';

export interface CreateCarProps extends DialogProps {
  onClose(): void;
}

const CreateCar: React.FC<CreateCarProps> = ({ onClose, ...props }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [createCar, createCarResponse] = useCreateCarMutation();

  const { values, errors, touched, handleSubmit, handleChange, setFieldValue } = useFormik<Car>({
    initialValues: {
      name: '',
      car_brand: null,
    },
    validationSchema: yup.object().shape({
      name: yup.string().required(messages.form.REQUIRED),
      car_brand: yup.object().nullable().required(messages.form.SELECT_FROM_LIST),
    }),
    async onSubmit(car, { resetForm }) {
      try {
        await createCar(car).unwrap();

        onClose();
        resetForm();
      } catch (error) {
        enqueueSnackbar(error.data, { variant: 'error' });
      }
    },
  });

  return (
    <Dialog fullWidth {...props}>
      <DialogTitle fontWeight="bold">Добавить модель автомобиля</DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item lg={6} md={6} xs={12}>
              <TextField
                name="name"
                label="Наименование"
                placeholder="Введите значение"
                value={values.name}
                onChange={handleChange}
                disabled={createCarResponse.isLoading}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name ? errors.name : ''}
                fullWidth
              />
            </Grid>

            <Grid item lg={6} md={6} xs={12}>
              <SearchCarBrands
                value={values.car_brand}
                onChange={(event, option) => setFieldValue('car_brand', option)}
                disabled={createCarResponse.isLoading}
                getOptionLabel={(option) => option.name}
                renderInput={(props) => (
                  <TextField
                    {...props}
                    name="car_brand"
                    label="Бренд"
                    placeholder="Выберите из списка"
                    error={Boolean(touched.car_brand && errors.car_brand)}
                    helperText={touched.car_brand && errors.car_brand}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            type="button"
            color="secondary"
            variant="outlined"
            size="large"
            disabled={createCarResponse.isLoading}
            onClick={onClose}
          >
            Закрыть
          </Button>

          <Button type="submit" variant="contained" size="large" disabled={createCarResponse.isLoading}>
            Добавить
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateCar;
